<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">
        <section class="intro">
          <p>
            Has creado tu tour virtual — ahora, ¿cómo hacer que trabaje para ti?
            Añadir tu tour a tu anuncio inmobiliario ya no es una opción
            diferenciadora: es una expectativa básica de los compradores. Aquí
            tienes cómo publicar y distribuir tu tour para un impacto máximo.
          </p>
        </section>

        <section>
          <h2>Paso 1 — Obtener el enlace desde Travvir</h2>
          <p>
            Abre tu tour en Travvir y pulsa el botón <strong>Compartir</strong>.
            Obtienes dos elementos clave:
          </p>
          <ul>
            <li>
              <strong>Enlace directo</strong> — URL corta que puedes copiar y
              pegar en cualquier lugar
            </li>
            <li>
              <strong>Código iFrame</strong> — bloque HTML para insertar el tour
              directamente en una página web
            </li>
          </ul>
          <p>
            Guarda ambos elementos en un documento de texto para usarlos al
            publicar el anuncio.
          </p>
        </section>

        <section>
          <h2>Paso 2 — Añadir el tour a tu anuncio MLS</h2>

          <h3>Localizar el campo Tour Virtual</h3>
          <p>
            La gran mayoría de plataformas MLS y portales inmobiliarios
            (Idealista, Fotocasa, Habitaclia, Pisos.com) disponen de un campo
            dedicado llamado «Tour virtual», «URL de visita», «Enlace de vídeo»
            o equivalente. Este campo acepta una URL directa.
          </p>

          <h3>Pegar el enlace de Travvir</h3>
          <p>
            Simplemente pega el enlace directo de tu tour en ese campo. La
            plataforma mostrará automáticamente un botón «Tour virtual» en el
            anuncio, visible para todos los compradores.
          </p>

          <h3>Verificar la visualización</h3>
          <p>
            Tras guardar, visualiza el anuncio como comprador para asegurarte de
            que el enlace funciona y el tour se abre correctamente.
          </p>
        </section>

        <section>
          <h2>Paso 3 — Distribución multicanal</h2>

          <h3>Portales y agregadores</h3>
          <p>
            Si tu CRM inmobiliario está conectado a varios portales, el enlace
            del tour se propaga automáticamente. Si no, actualiza cada portal
            manualmente — la operación tarda 5 minutos por plataforma.
          </p>

          <h3>Tu sitio web</h3>
          <p>
            Inserta el código iFrame directamente en la página de la propiedad
            de tu web. Esto mejora tu SEO local (tiempo en página) y ofrece una
            experiencia premium a tus visitantes.
          </p>

          <h3>E-mail a prospectos</h3>
          <p>
            Envía el enlace directo a tu lista de contactos interesados en
            propiedades similares. Los e-mails con enlace de tour virtual
            muestran tasas de apertura 2× superiores a la media.
          </p>

          <h3>Redes sociales</h3>
          <p>
            Comparte el enlace en tu página profesional de Facebook, LinkedIn e
            Instagram. Acompáñalo de una captura del tour para llamar la
            atención en el feed.
          </p>
        </section>

        <section>
          <h2>Buenas prácticas para maximizar el engagement</h2>
          <ul>
            <li>
              Menciona explícitamente «Tour virtual disponible» en el título del
              anuncio
            </li>
            <li>
              Coloca el enlace al principio de la descripción, no solo al final
            </li>
            <li>
              Crea una miniatura de vista previa atractiva como primera foto
            </li>
            <li>
              Actualiza el enlace si vuelves a filmar habitaciones adicionales
            </li>
          </ul>
        </section>
        <section class="continue-reading">
          <h2>Seguir Leyendo</h2>
          <ul>
          <li><a href="/blog/es/recorrido-virtual-inmobiliario">Cómo crear un recorrido virtual inmobiliario con tu smartphone en 2026</a></li>
          <li><a href="/blog/es/integrar-tour-virtual-sitio-web">Cómo Integrar un Tour Virtual 360° en tu Sitio Web 2026</a></li>
          <li><a href="/blog/es/agente-inmobiliario-tour-virtual-2026">Agente Inmobiliario y Tour Virtual: Por Qué es Imprescindible en 2026</a></li>
          <li><a href="/blog/es/tour-virtual-vs-fotos-inmobiliaria">Tour Virtual vs Fotos Inmobiliarias: ¿Qué Diferencia de Rendimiento?</a></li>
          <li><a href="/blog/es/tour-virtual-inmobiliario-smartphone">Tour Virtual Inmobiliario con Smartphone: Guía Completa 2026</a></li>
          <li><a href="/blog/es/tour-virtual-360-guia-completa">Tour Virtual 360°: Guía Completa para Principiantes (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "añadir-tour-virtual-anuncio",
);

useBlogSeo({
  title: "Añadir un Tour Virtual a tu Anuncio Inmobiliario | Travvir",
  description:
    "Guía completa para añadir y distribuir tu tour virtual: MLS, portales inmobiliarios, web y redes sociales. Maximiza la visibilidad de tus anuncios.",
  keywords:
    "añadir tour virtual anuncio inmobiliario, enlace tour virtual MLS, distribuir tour virtual inmobiliario",
  slug: "añadir-tour-virtual-anuncio",
  image: postData?.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question: "¿Cómo añadir un tour virtual a un anuncio MLS?",
      answer:
        "Obtén el enlace de compartir desde Travvir y pégalo en el campo «Tour virtual» o «URL de visita» del portal MLS.",
    },
    {
      question: "¿Puedo actualizar el tour después de publicar el anuncio?",
      answer:
        "Sí — el enlace de Travvir permanece igual si modificas el tour. Los portales mostrarán automáticamente la versión actualizada.",
    },
  ],
});

</script>
